// Closing Cost page -> MISMO 3.4 LOAN/FEE_INFORMATION/FEES.
//
// Each line item becomes one <FEE> carrying its MISMO FeeType, the amount, and
// an IntegratedDisclosureSectionType saying which TRID section it belongs to.
// The LANIS Closing Cost sections map 1:1 onto that enum:
//
//   A Origination Charges          -> OriginationCharges
//   B Services You Cannot Shop For -> ServicesYouCannotShopFor
//   C Services You Can Shop For    -> ServicesYouCanShopFor
//   E Taxes and Other Gov Fees     -> TaxesAndOtherGovernmentFees
//   F Prepaids                     -> Prepaids
//   G Initial Escrow at Closing    -> InitialEscrowPaymentAtClosing
//   H Other                        -> Other
//   D / I / J totals               -> TotalLoanCosts / TotalOtherCosts / TotalClosingCosts
//
// NOTE: fees are TRID (Loan Estimate / Closing Disclosure) data, not DU
// underwriting data — DU ignores this block. It is here to populate an LOS
// Loan Estimate.

import Decimal from "decimal.js";

import ORIGINATION_FEES from "../../constants/origination_fees";
import SERVICES_YOU_CANNOT_SHOP_FOR from "../../constants/services_you_cannot_shop_for";
import SERVICES_YOU_CAN_SHOP_FOR from "../../constants/services_you_can_shop_for";
import OTHER_SERVICES from "../../constants/other_services";

import {
  loan_origination_fee,
  mortgage_broker_fee,
  recording_fees_and_other_taxes,
  transfer_tax_purchase,
  prepaids_homeowners_insurance_premium,
  prepaids_mortgage_insurance_premium,
  prepaids_property_taxes,
  total_prepaids,
  initial_escrow_payment_at_closing_homeowners_insurance,
  initial_escrow_payment_at_closing_mortgage_insurance,
  initial_escrow_payment_at_closing_property_taxes,
  total_loan_costs,
  total_other_costs,
  total_closing_costs
} from "../../calculations/loan";

const SECTION = {
  A: "OriginationCharges",
  B: "ServicesYouCannotShopFor",
  C: "ServicesYouCanShopFor",
  E: "TaxesAndOtherGovernmentFees",
  F: "Prepaids",
  G: "InitialEscrowPaymentAtClosing",
  H: "Other"
};

/**
 * LANIS fee label -> MISMO FeeType. Matched on a normalized label so it works
 * across all four fee lists. Anything not listed exports as FeeType "Other"
 * with the LANIS label in FeeTypeOtherDescription, so no fee is ever dropped
 * and its name always survives.
 */
const FEE_TYPE_BY_LABEL = {
  "loan origination fee": "LoanOriginationFee",
  "origination fee": "LoanOriginationFee",
  "mortgage broker fee": "MortgageBrokerFee",
  "broker flat fee": "MortgageBrokerFee",
  "application fee": "ApplicationFee",
  // "Administration Fee" has no MISMO equivalent — falls back to Other +
  // FeeTypeOtherDescription, which preserves the name.
  "processing fee": "ProcessingFee",
  "third party processing fee": "ProcessingFee",
  "third party loan processing fee": "ProcessingFee",
  "underwriting fee": "UnderwritingFee",
  "appraisal fee": "AppraisalFee",
  "appraisal fee poc": "AppraisalFee",
  "appraisal desk review fee": "AppraisalDeskReviewFee",
  "appraisal field review fee": "AppraisalFieldReviewFee",
  "assumption fee": "AssumptionFee",
  "commitment fee": "CommitmentFee",
  "courier messenger fee": "CourierFee",
  "courier fee": "CourierFee",
  "credit report fee": "CreditReportFee",
  "escrow waiver fee": "EscrowWaiverFee",
  "flood certification fee": "FloodCertification",
  "flood determination fee": "FloodCertification",
  // Flood monitoring and lender inspection have no MISMO equivalent -> Other.
  "lender doc prep fee": "DocumentPreparationFee",
  "mers registration fee": "MERSRegistrationFee",
  "modification fee": "ModificationFee",
  "rate lock fee": "RateLockFee",
  "signing agent fee": "NotaryFee",
  "notary fee": "NotaryFee",
  "tax service fee": "TaxServiceFee",
  "tax monitoring fee": "TaxServiceFee",
  "tax status research fee": "TaxStatusResearchFee",
  "survey fee": "SurveyFee",
  "title abstract title search fee": "TitleAbstractFee",
  "title closing fee escrow fee": "TitleClosingFee",
  "title lender s title policy": "TitleInsuranceFee",
  "title lenders title policy": "TitleInsuranceFee",
  "title document signing fee": "TitleDocumentPreparationFee",
  "title lender endorsements": "TitleEndorsementFee",
  "title recording fee": "RecordingFeeTotal",
  "title wire disbursement fee": "WireTransferFee",
  "wire transfer fee": "WireTransferFee",
  "condo certification": "CondominiumAssociationDues",
  "hoa certification": "HomeownersAssociationDues"
};

function normalizeLabel(label) {
  return String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function feeTypeFor(label) {
  return FEE_TYPE_BY_LABEL[normalizeLabel(label)] || null;
}

function toAmount(value) {
  const n = value instanceof Decimal ? value : new Decimal(parseFloat(value) || 0);
  return n.toDecimalPlaces(2);
}

/** Write one <FEE>. Skips zero/blank amounts so the file stays clean. */
function writeFee(builder, { label, amount, section }) {
  const value = toAmount(amount);
  if (value.lessThanOrEqualTo(0)) return;

  const fee = builder.ele("FEE");
  const detail = fee.ele("FEE_DETAIL");

  const mismoType = feeTypeFor(label);
  detail.ele("FeeType").txt(mismoType || "Other");
  if (!mismoType && label) {
    detail.ele("FeeTypeOtherDescription").txt(String(label));
  }
  if (label) {
    detail.ele("FeeDescription").txt(String(label));
  }
  detail.ele("FeeEstimatedTotalAmount").txt(value.toString());
  detail.ele("IntegratedDisclosureSectionType").txt(section);
}

/** Write every entry of a { feeId: amount } map using its label list. */
function writeFeeMap(builder, feeMap, labels, section) {
  Object.entries(feeMap || {}).forEach(([feeId, amount]) => {
    writeFee(builder, { label: labels[feeId] || feeId, amount, section });
  });
}

/** Only skip when there is no selected loan to price. */
export function skipFeeInformation({ loan }) {
  return !loan;
}

export function populateFeeInformation(builder, { document, options, loan }) {
  if (!loan) return;

  const documentType = options && options.documentType;
  const property = document.property;
  const propertyToBeSold = document.property_to_be_sold;
  const isSellingProperty = document.is_selling_property;
  const args = [documentType, loan, property, propertyToBeSold, isSellingProperty];

  const fees = builder.ele("FEES");

  // --- A. Origination Charges -------------------------------------------
  writeFee(fees, {
    label: "Loan Origination Fee",
    amount: loan_origination_fee(...args),
    section: SECTION.A
  });
  writeFee(fees, {
    label: "Mortgage Broker Fee",
    amount: mortgage_broker_fee(...args),
    section: SECTION.A
  });
  writeFeeMap(fees, loan.origination_fees, ORIGINATION_FEES, SECTION.A);

  // --- B / C. Services ---------------------------------------------------
  writeFeeMap(
    fees,
    loan.services_you_cannot_shop_for,
    SERVICES_YOU_CANNOT_SHOP_FOR,
    SECTION.B
  );
  writeFeeMap(
    fees,
    loan.services_you_can_shop_for,
    SERVICES_YOU_CAN_SHOP_FOR,
    SECTION.C
  );

  // --- E. Taxes and Other Government Fees --------------------------------
  writeFee(fees, {
    label: "Recording Fees and Other Taxes",
    amount: recording_fees_and_other_taxes(loan),
    section: SECTION.E
  });
  writeFee(fees, {
    label: "Transfer Tax",
    amount: transfer_tax_purchase(documentType, loan, property),
    section: SECTION.E
  });

  // --- F. Prepaids -------------------------------------------------------
  const hoiPrepaid = toAmount(
    prepaids_homeowners_insurance_premium(documentType, loan, property)
  );
  const miPrepaid = toAmount(prepaids_mortgage_insurance_premium(...args));
  const taxPrepaid = toAmount(
    prepaids_property_taxes(documentType, loan, property)
  );
  writeFee(fees, {
    label: "Homeowner's Insurance Premium",
    amount: hoiPrepaid,
    section: SECTION.F
  });
  writeFee(fees, {
    label: "Mortgage Insurance Premium",
    amount: miPrepaid,
    section: SECTION.F
  });
  writeFee(fees, {
    label: "Property Taxes",
    amount: taxPrepaid,
    section: SECTION.F
  });
  // Prepaid interest is the remainder of the section (LANIS derives it in the
  // prepaids calculator rather than storing it as a line item).
  const prepaidInterest = toAmount(total_prepaids(...args))
    .minus(hoiPrepaid)
    .minus(miPrepaid)
    .minus(taxPrepaid);
  writeFee(fees, {
    label: "Prepaid Interest",
    amount: prepaidInterest,
    section: SECTION.F
  });

  // --- G. Initial Escrow Payment at Closing ------------------------------
  writeFee(fees, {
    label: "Homeowner's Insurance",
    amount: initial_escrow_payment_at_closing_homeowners_insurance(
      documentType,
      loan,
      property
    ),
    section: SECTION.G
  });
  writeFee(fees, {
    label: "Mortgage Insurance",
    amount: initial_escrow_payment_at_closing_mortgage_insurance(...args),
    section: SECTION.G
  });
  writeFee(fees, {
    label: "Property Taxes",
    amount: initial_escrow_payment_at_closing_property_taxes(
      documentType,
      loan,
      property
    ),
    section: SECTION.G
  });

  // --- H. Other ----------------------------------------------------------
  writeFeeMap(fees, loan.other_services, OTHER_SERVICES, SECTION.H);

  // --- Totals -------------------------------------------------------------
  // FEE_SUMMARY_DETAIL carries a single grand total (FeeSummaryTotalFeesAmount);
  // there are no per-section total elements in B324. The TRID section totals
  // (D / I / J) are instead expressed as FEEs whose section IS the total, which
  // is what the enum values TotalLoanCosts / TotalOtherCosts / TotalClosingCosts
  // are for — a consumer reads those rather than re-summing.
  writeFee(fees, {
    label: "Total Loan Costs (D)",
    amount: total_loan_costs(...args),
    section: "TotalLoanCosts"
  });
  writeFee(fees, {
    label: "Total Other Costs (I)",
    amount: total_other_costs(...args),
    section: "TotalOtherCosts"
  });
  writeFee(fees, {
    label: "Total Closing Costs (J)",
    amount: total_closing_costs(...args),
    section: "TotalClosingCosts"
  });

  const summary = builder.ele("FEES_SUMMARY");
  summary
    .ele("FEE_SUMMARY_DETAIL")
    .ele("FeeSummaryTotalFeesAmount")
    .txt(toAmount(total_closing_costs(...args)).toString());
}
